import styled from "styled-components";
import { useParams } from "react-router-dom";
import useQueryCustom from "hooks/useQueryCustom";
import { Controller, useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { currentUserSelector } from "redux/selectors";
import UniversalInput from "components/Input/UniversalInput";
import SelectWithLocationAdd from "components/Dropdowns/SelectWithLocationAdd";
import Checkbox from "components/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import SignatureTypes from "../../SignatureComponent";
import { createLocation, getLocations } from "services";
import { toDataURL } from "utils/common";

import { StepTitle } from "../../StepsContent/Welcome";
import { FormI9ContentContainer, I9ExampleContainer, FormI9FieldsContainer } from "../../StepsContent/FormI9";
import { FieldItem } from "./DocumentInformation";
import i9Example from 'assets/img/i-9_example.png';

export default function FormI9() {
    const { id } = useParams<{ id: string }>();
    const { company } = useSelector(currentUserSelector);
    const { register, control, setValue, formState: { errors } } = useFormContext();

    const { isLoading } = useQueryCustom<any>(["get_onboarding_admin_i9"], {
        endpoint: `onboarding/admin/i9?employee_id=${id}`,
        options: { method: "get" },
        onSuccess: (data) => {
            if (data.i9) {
                setValue('job_title', data.i9.job_title);
                setValue('location', data.i9.location);
                setValue('terms_agreed', data.i9.terms_agreed);
            };
            if (data.signature.type === 'text') {
                setValue('signature', { signature: data.signature.signature, type: data.signature.type });
            };

            const signature_img = data?.signature.uuid ? `${process.env.REACT_APP_BASE_API_URL}document/signature/${company.id}/${data?.signature.uuid}` : null;
            if (signature_img && data.signature.type === 'draw') {
                toDataURL(signature_img).then(dataUrl => {
                    setValue('signature', { signature: dataUrl, type: data.signature.type, })
                });
            };
            if (signature_img && data.signature.type === 'upload') {
                setValue('signature', { signature: signature_img, type: data.signature.type, })
            };
        }
    }, { refetchOnWindowFocus: false });

    const { refetch: getI9Preview, isFetching: previewI9Loading } = useQueryCustom<any>(["get_onboarding_admin_i9_preview"], {
        endpoint: `/onboarding/admin/i9_preview?employee_id=${id}`,
        options: { method: "get", responseType: 'arraybuffer', timeout: 30000 },
        onSuccess: (data) => {
            let blob = new Blob([data], { type: 'application/pdf' });
            let url = URL.createObjectURL(blob);
            window.open(url);
        },
    }, { enabled: false });

    if (isLoading) return <LoadingScreenContainer><CircularProgress thickness={4} /></LoadingScreenContainer>;

    return (
        <ContentContainer>
            <StepTitle>Sign Form I-9</StepTitle>
            <FormI9ContentContainer>
                <I9ExampleContainer onClick={() => getI9Preview()}>
                    <img src={i9Example} alt="i-9 example" />
                    <p>Review document</p>
                    {previewI9Loading ? <LinearProgress /> : null}
                </I9ExampleContainer>
                <FormI9FieldsContainer>
                    <FieldItem label="Your Title" required style={{ maxWidth: 600 }}>
                        <UniversalInput
                            {...register('job_title', { required: "Your Title is required" })}
                            errorText={errors.job_title?.message}
                        />
                    </FieldItem>
                    <FieldItem label="Business or Organization Address" required style={{ maxWidth: 600 }}>
                        <Controller
                            name="location"
                            control={control}
                            rules={{ required: "Business or Organization Address is required" }}
                            render={({ field: { value, onChange } }) => (
                                <SelectWithLocationAdd
                                    name='location'
                                    inputValue={value}
                                    loadRemoteData={() => getLocations(100, 1, false, false)}
                                    createRequest={createLocation}
                                    onChange={onChange}
                                    errorText={errors.location?.message}
                                />
                            )}
                        />
                    </FieldItem>
                    <div style={{ marginBottom: 15 }}>
                        <Controller
                            control={control}
                            rules={{ validate: value => !!value.signature || 'Your signature is required' }}
                            name="signature"
                            render={({ field: { onChange, value } }) => (
                                <SignatureTypes
                                    label='Signature'
                                    required
                                    activeTab={value.type}
                                    onChange={onChange}
                                    value={value}
                                    helperText={errors.signature?.message as string}
                                    isErrorText
                                />
                            )}
                        />
                    </div>
                    <FieldItem label="" required style={{ maxWidth: 600 }}>
                        <Controller
                            control={control}
                            rules={{ required: 'Your agreement is required' }}
                            name="terms_agreed"
                            render={({ field: { onChange, value } }) => (
                                <Checkbox
                                    checked={value}
                                    onChange={onChange}
                                    label={'I agree to electronically sign this form. I attest, under penalty of perjury, that (1) I have examined the document(s) presented by the above-named employee, (2) the above-listed document(s) appear to genuine and to relate to the employee named, and (3) to the best of my knowledge the employee is authorized to work in the United States.'}
                                    errorText={errors.terms_agreed?.message}
                                />
                            )}
                        />
                    </FieldItem>
                </FormI9FieldsContainer>
            </FormI9ContentContainer>
        </ContentContainer>
    )
};

const ContentContainer = styled.div`
    overflow-y: auto;
    flex: 1;
    padding-top: 60px;
`;

const LoadingScreenContainer = styled.div`
   display: flex;
   justify-content: center;
   align-items: center;
   flex: 1;
`;